import { ChangePasswordOnReset } from '../../../app/models/custom/ChangePasswordOnReset';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, finalize, from, map, Observable, of, switchMap } from 'rxjs';
import { AuthSoftLoginResult } from '@c3library/models/auth/auth-soft-login-result';
import { environment } from '@environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AuthLogin } from '@c3library/models/auth/auth-login';
import { AuthLoginResult } from '@c3library/models/auth/auth-login-result';
import { MsalService } from '@azure/msal-angular';
import { Router } from '@angular/router';
import { BaseService } from '../../../app/services/api/base/BaseService';

@Injectable({
  providedIn: 'root',
})
export class C3AuthService extends BaseService {

  private authLocalStorageToken = 'AuthUser';

  // private fields
  private urlPart: string = 'Auth';

  // public fields
  isLoadingSubject = new BehaviorSubject<boolean>(false);
  currentUserSubject = new BehaviorSubject<AuthLoginResult | AuthSoftLoginResult | undefined>(undefined);
  // chewyUser = toSignal(this.currentUserSubject);

  //  chewy - get rid of this
  // updateUserType(userType: UserType) {    
  //   const user = this.getStoredAuthUser();
  //   if (user) {
  //     const changedUser = {
  //       ...user,
  //       user: {
  //         ...user.user,
  //         userType: userType,
  //       },
  //     }
  //     this.currentUserSubject.next(changedUser);
  //   }
  // }

  constructor(
    private http: HttpClient,
    private msalService: MsalService,
    private router: Router
  ) {
    super(http, router, null);
    this.apiSegmentUrl = this.urlPart;
    


    const user = this.getStoredAuthUser();
    if (user) {
      this.currentUserSubject.next(user);
    }
  }

  get currentUserValue(): AuthLoginResult | AuthSoftLoginResult | undefined {
    return this.currentUserSubject.value;
  }

  set currentUserValue(user: AuthLoginResult | AuthSoftLoginResult | undefined) {
    this.currentUserSubject.next(user);
  }

  public isAuthenticated(): boolean {
    const storedAuth = this.getStoredAuthUser();
    if (storedAuth?.success) {
      return true;
    }

    return this.currentUserSubject.value?.success === true;
  }

  public login(loginDetail: AuthLogin): Observable<AuthLoginResult> {
    // login
    this.isLoadingSubject.next(true);

    //const url = '/login';
    const url = `${super.getBaseUrl()}/login`;

    if (!environment.production)
      console.log("Login:Calling " + url);

    return this.http.post<AuthLogin>(url, loginDetail)
      .pipe(
        map(next => {
          let o: AuthLoginResult = Object.assign(new AuthLoginResult(), next);
          if (!this.storeAuthUserRecordInLocalStorage(o)) return new AuthLoginResult();
          this.currentUserSubject.next(o);
          return o;
        }),
        finalize(() => {
          this.isLoadingSubject.next(false);
        })
      );
  }

  private storeAuthUserRecordInLocalStorage(auth: AuthLoginResult): boolean {

    if ((auth != null) && (auth.success)) {
      localStorage.setItem(this.authLocalStorageToken, JSON.stringify(auth));
      return true;
    }

    return false;
  }

  public handleLoginSuccess(auth: AuthLoginResult): boolean {
    if (auth && auth.success) {
      let o: AuthLoginResult = Object.assign(new AuthLoginResult(), auth);
      if (this.storeAuthUserRecordInLocalStorage(o)) {
        this.currentUserSubject.next(o);
        return true;
      }
    }
    return false;
  }

  // public softlogin(username: string): Observable<AuthSoftLoginResult> {
  //   // login
  //   this.isLoadingSubject.next(true);

  //   const url = `https://localhost:7047/Auth/softlogin`;

  //   // if (!environment.production)
  //   //   console.log("Login:Calling " + url);

  //   return this.http.post<AuthSoftLoginResult>(url, { username })
  //     .pipe(
  //       map(next => {
  //         let o: AuthSoftLoginResult = Object.assign(new AuthSoftLoginResult(), next);
  //         if (!this.storeMSALAuthUserRecordInLocalStorage(o)) return new AuthSoftLoginResult();
  //         this.currentUserSubject.next(o);
  //         return o;
  //       }),
  //       //catchError(),
  //       finalize(() => {
  //         this.isLoadingSubject.next(false);
  //       })
  //     );
  // }

  public refreshToken(loginDetail: AuthLoginResult | AuthSoftLoginResult): Observable<AuthLoginResult> {
    // refreshToken
    this.isLoadingSubject.next(true);

    const url = `${super.getBaseUrl()}/refreshtoken`;

    if (!environment.production)
      console.log("Login:Calling " + url);

    return this.http.post<AuthLoginResult>(url, loginDetail)
      .pipe(
        map(next => {
          let o: AuthLoginResult = Object.assign(new AuthLoginResult(), next);
          if (!this.storeAuthUserRecordInLocalStorage(o)) return new AuthLoginResult();
          this.currentUserSubject.next(o);
          return o;
        }),
        catchError(this.errorHandler),
        finalize(() => this.isLoadingSubject.next(false))
      );
  }


  public softlogin(username: string): Observable<AuthSoftLoginResult> {
    this.isLoadingSubject.next(true);
    const url = environment.apiBaseUrl + '/Auth/softlogin';

    const account = this.msalService.instance.getActiveAccount();
    if (!account) throw new Error("No MSAL account active");

    return from(
      this.msalService.acquireTokenSilent({
        scopes: [environment.apiScope],
        account
      })
    ).pipe(
      switchMap(result => {
        const headers = new HttpHeaders({
          Authorization: `Bearer ${result.accessToken}`
        });
        return this.http.post<AuthSoftLoginResult>(url, { username }, { headers });
      }),
      map(next => {
        let o: AuthSoftLoginResult = Object.assign(new AuthSoftLoginResult(), next);
        if (!this.storeMSALAuthUserRecordInLocalStorage(o)) return new AuthSoftLoginResult();
        this.currentUserSubject.next(o);
        return o;
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  private storeMSALAuthUserRecordInLocalStorage(auth: AuthSoftLoginResult): boolean {

    if ((auth != null) && (auth.success)) {
      localStorage.setItem(this.authLocalStorageToken, JSON.stringify(auth));
      return true;
    }

    return false;
  }

  private getStoredAuthUser(): AuthLoginResult | AuthSoftLoginResult | undefined {
    try {
      const lsValue = localStorage.getItem(this.authLocalStorageToken);
      if (!lsValue) {
        return undefined;
      }

      return JSON.parse(lsValue) as AuthSoftLoginResult;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  // TODO below copied from franklins auth service

  public logout(): Observable<boolean> {
    // logout
    this.isLoadingSubject.next(true);

    localStorage.removeItem(this.authLocalStorageToken);
    this.redirectToLogin();

    this.isLoadingSubject.next(false);

    console.log("logout setting to undefined");
    this.currentUserSubject.next(undefined);

    return of(true);

  }

  public redirectToLogin(): void {
    this.router.navigate(['/login']);
  }


  public verifyResetGuid(guid: string): Observable<boolean> {
    const url = `${super.getBaseUrl()}/verify-reset-guid/${guid}`;
    return this.http.get<boolean>(url).pipe(
      map((next) => next),
      catchError(this.errorHandler)
    );
  }

  public verifyCreateGuid(guid: string): Observable<boolean> {
    const url = `${super.getBaseUrl()}/verify-create-guid/${guid}`;
    return this.http.get<boolean>(url).pipe(
      map((next) => next),
      catchError(this.errorHandler)
    );
  }

  public changePasswordOnReset(passwordDetails: ChangePasswordOnReset): Observable<AuthLoginResult> {
    const url = `${super.getBaseUrl()}/change-password-on-reset`;
    return this.http.put<AuthLoginResult>(url, passwordDetails).pipe(
      map((next) => next),
      catchError(this.errorHandler)
    );
  }

  public createPasswordFirstLogin(passwordDetails: ChangePasswordOnReset): Observable<AuthLoginResult> {
    const url = `${super.getBaseUrl()}/create-password-first-login`;
    return this.http.put<AuthLoginResult>(url, passwordDetails).pipe(
      map((next) => next),
      catchError(this.errorHandler)
    );
  }
}
